import React from 'react';
import AtsCarousel from '../../../components/ATS/Carousel/AtsCarousel';
import { AuthProtected } from '../../../components/auth-protected';
import { ErrorBoundary } from '../../../components/error-boundary';
import { isDev } from '../../../config';
import { Layout } from '../../../queries/ats/layout/types';
import { warn } from '../../../utils/commonUtils';
import { NotFound } from '../../not-found/not-found';
import GetAccessContainer from '../../streamingServices/GetAccessContainer';
import styles from './section-type.module.scss';
import { Favorites } from './section-types/Favorites/Favorites';
import { SectionBought } from './section-types/section-bought-and-rented/section-bought';
import { SectionBoughtAndRented } from './section-types/section-bought-and-rented/section-bought-and-rented';
import { SectionRented } from './section-types/section-bought-and-rented/section-rented';
import { SectionContinueWatching } from './section-types/SectionContinueWatching/SectionContinueWatching';
import { SectionGrid } from './section-types/SectionGrid';
import { PaginatedSectionGrid } from './section-types/SectionGrid/paginated-section-grid';
import { SectionLivePrograms } from './section-types/SectionLivePrograms/SectionLivePrograms';
import { SectionMyRecordings } from './section-types/SectionMyRecordings/SectionMyRecordings';
import { SectionSwimlane } from './section-types/SectionSwimlane';
import { SectionUserChannelsCatchup } from './section-types/SectionUserChannelsCatchup/SectionUserChannelsCatchup';
import { SectionUserChannelsOngoing } from './section-types/SectionUserChannelsOngoing/SectionUserChannelsOngoing';

interface SectionTypeProps {
  view: Layout.SectionView;
  data: Layout.SectionItem[];
  pageStyle: 'grid' | 'swimlane';
}

export function SectionType({ view, data, pageStyle }: SectionTypeProps) {
  const {
    CAROUSEL,
    SWIMLANE,
    SORTABLE_GRID,
    PLAIN_GRID,
    USER_CHANNELS_ONGOING,
    USER_CHANNELS_CATCHUP,
    LIVE_PROGRAMS,
    CONTINUE_WATCHING,
    BOUGHT_AND_RENTED,
    MY_RECORDINGS,
    FAVOURITES,
    FAVORITES,
    BOUGHT,
    RENTED,
    STREAMING_SERVICES_FOOTER,
  } = Layout.SectionStyleType;

  if (!view) {
    console.error('View is undefined');
    return <NotFound />;
  }

  switch (view.style.type) {
    case CAROUSEL:
      return <AtsCarousel view={view} data={data} interval={7500} />;
    case SWIMLANE:
      return <SectionSwimlane view={view} data={data} />;
    case PLAIN_GRID:
    case SORTABLE_GRID:
      return <PaginatedSectionGrid view={view} data={data} />;
    case STREAMING_SERVICES_FOOTER:
      return <GetAccessContainer />;
    case FAVOURITES:
    case FAVORITES:
      return <Favorites className={styles.container} view={view} data={data} />;
    case USER_CHANNELS_CATCHUP:
      return <SectionUserChannelsCatchup view={view} className={styles.container} pageStyle={pageStyle} />;
    case USER_CHANNELS_ONGOING:
      return <SectionUserChannelsOngoing view={view} className={styles.container} pageStyle={pageStyle} />;
    case LIVE_PROGRAMS:
      return <SectionLivePrograms view={view} className={styles.container} />;
    case CONTINUE_WATCHING:
      return (
        <AuthProtected type="user">
          <ErrorBoundary>
            <SectionContinueWatching className={styles.container} view={view} />
          </ErrorBoundary>
        </AuthProtected>
      );
    case BOUGHT:
      return (
        <AuthProtected type={['home', 'user']}>
          <ErrorBoundary>
            <SectionBought view={view} pageStyle={pageStyle} />
          </ErrorBoundary>
        </AuthProtected>
      );
    case RENTED:
      return (
        <AuthProtected type={['home', 'user']}>
          <ErrorBoundary>
            <SectionRented view={view} pageStyle={pageStyle} />
          </ErrorBoundary>
        </AuthProtected>
      );
    case BOUGHT_AND_RENTED:
      return (
        <AuthProtected type={['home', 'user']}>
          <ErrorBoundary>
            <SectionBoughtAndRented view={view} pageStyle={pageStyle} />
          </ErrorBoundary>
        </AuthProtected>
      );
    case MY_RECORDINGS:
      return <SectionMyRecordings className={styles.container} view={view} data={data} />;

    default:
      return isDev ? (
        <span>
          <small>Label: </small>
          {view.label.toUpperCase()} - <small> Style type:</small> {view.style.type.toUpperCase()} -{' '}
          <small>Pattern: </small> {view.style.pattern.toUpperCase()}
        </span>
      ) : null;
  }
}

export function SectionTypeSkeleton({ view, pageStyle }: Pick<SectionTypeProps, 'view' | 'pageStyle'>) {
  const {
    CAROUSEL,
    SWIMLANE,
    SORTABLE_GRID,
    PLAIN_GRID,
    CHANNEL_FILTERABLE_GRID,
    USER_CHANNELS_ONGOING,
    USER_CHANNELS_CATCHUP,
    LIVE_PROGRAMS,
    CONTINUE_WATCHING,
    BOUGHT_AND_RENTED,
    MY_RECORDINGS,
    FAVOURITES,
    FAVORITES,
    BOUGHT,
    RENTED,
    STREAMING_SERVICES_FOOTER,
  } = Layout.SectionStyleType;

  switch (view.style.type) {
    case CAROUSEL:
      return <AtsCarousel.Skeleton view={view} />;
    case SWIMLANE:
      return <SectionSwimlane.Skeleton view={view} />;
    case PLAIN_GRID:
    case SORTABLE_GRID:
      return <SectionGrid.Skeleton view={view} />;
    case STREAMING_SERVICES_FOOTER:
      warn('TODO: Implement GetAccessContainer skeleton');
      return null;
    case FAVOURITES:
    case FAVORITES:
      warn('TODO: Implement Favorites skeleton');
      return null;
    case CHANNEL_FILTERABLE_GRID:
      warn('TODO: Implement ChannelGrid skeleton');
      return null;
    case USER_CHANNELS_CATCHUP:
      return <SectionUserChannelsCatchup.Skeleton view={view} pageStyle={pageStyle} className={styles.container} />;
    case USER_CHANNELS_ONGOING:
      return <SectionUserChannelsOngoing.Skeleton view={view} pageStyle={pageStyle} className={styles.container} />;
    case LIVE_PROGRAMS:
      return <SectionLivePrograms.Skeleton view={view} />;
    case CONTINUE_WATCHING:
      return <SectionSwimlane.Skeleton view={view} />;
    case BOUGHT:
      return <SectionSwimlane.Skeleton view={view} />;
    case RENTED:
      return <SectionSwimlane.Skeleton view={view} />;
    case BOUGHT_AND_RENTED:
      return <SectionSwimlane.Skeleton view={view} />;
    case MY_RECORDINGS:
      return <SectionMyRecordings.Skeleton className={styles.container} view={view} />;
    default:
      return null;
  }
}

SectionType.Skeleton = SectionTypeSkeleton;
